import React, { useEffect, useState } from "react";
import { Link, Route, Routes, useNavigate, useParams } from "react-router-dom";
import { House, Package, ChatCircle, Wallet, ChartLineUp, Plus, CheckCircle, Star, ShieldCheck, Info, PencilSimple, Gear, Warning, Trash } from "@phosphor-icons/react";
import DashboardShell from "@/components/DashboardShell";
import { api, formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import OrderDetail from "@/pages/OrderDetail";
import ServiceForm from "@/components/ServiceForm";
import Avatar from "@/components/Avatar";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/dashboard/seller", end: true, label: "Overview", Icon: House },
  { to: "/dashboard/seller/services", label: "My services", Icon: Package },
  { to: "/dashboard/seller/services/new", label: "Create service", Icon: Plus },
  { to: "/dashboard/seller/orders", label: "Orders", Icon: ChartLineUp },
  { to: "/dashboard/seller/earnings", label: "Earnings", Icon: Wallet },
  { to: "/messages", label: "Messages", Icon: ChatCircle },
  { to: "/dashboard/seller/settings", label: "Settings", Icon: Gear },
];

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5">
      <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wider font-bold"><Icon weight="duotone" /> {label}</div>
      <div className="font-display font-black text-3xl text-mv-secondary mt-2">{value}</div>
    </div>
  );
}
function Card({ title, children, action }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="font-display font-bold text-mv-secondary">{title}</div>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Overview() {
  const [orders, setOrders] = useState([]);
  const [profile, setProfile] = useState(null);
  const [trust, setTrust] = useState(null);
  useEffect(() => {
    api.get("/orders").then(({ data }) => setOrders(data));
    api.get("/payments/payouts/my").then(({ data }) => setProfile(data));
    api.get("/sellers/me/trust").then(({ data }) => setTrust(data)).catch(() => {});
  }, []);
  const active = orders.filter((o) => !["completed", "cancelled", "refunded"].includes(o.status));
  const completed = orders.filter((o) => o.status === "completed");
  return (
    <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Active orders" value={active.length} icon={Package} />
        <Stat label="Completed" value={completed.length} icon={CheckCircle} />
        <Stat label="Balance" value={`$${(profile?.earnings_balance || 0).toFixed(0)}`} icon={Wallet} />
        <Stat label="Lifetime" value={`$${(profile?.lifetime_earnings || 0).toFixed(0)}`} icon={ChartLineUp} />
      </div>
      {trust && <TrustPanel trust={trust} />}
      <Card title="Recent orders">
        <OrderTable orders={orders.slice(0, 8)} />
      </Card>
    </>
  );
}

const _BAND_TONE = {
  strong: "bg-mv-primary/10 text-mv-primary border-mv-primary/30",
  ok:     "bg-gray-100 text-gray-700 border-gray-200",
  watch:  "bg-amber-50 text-amber-800 border-amber-200",
};
const _BAND_LABEL = { strong: "Strong", ok: "OK", watch: "Watch" };

function TrustPanel({ trust }) {
  const { score, label, is_new, hints, guidance } = trust;
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid="seller-trust-panel">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} weight="duotone" className="text-mv-primary" />
          <span className="font-display font-bold text-mv-secondary">Your Trust Score</span>
        </div>
        {is_new ? (
          <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-gray-100 text-gray-600" data-testid="trust-new-state">
            New seller · Not enough data yet
          </span>
        ) : (
          <div className="flex items-baseline gap-2">
            <span className="font-display font-black text-3xl text-mv-secondary">{score}<span className="text-gray-400 text-lg">/100</span></span>
            <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-mv-primary text-white">{label}</span>
          </div>
        )}
      </div>
      <div className="p-5 space-y-4">
        <p className="text-sm text-gray-600 flex gap-2" data-testid="trust-guidance">
          <Info size={16} weight="duotone" className="text-mv-primary shrink-0 mt-0.5" />
          <span>{guidance}</span>
        </p>
        <div className="grid sm:grid-cols-2 gap-3" data-testid="trust-hints">
          {hints.map((h) => (
            <div key={h.key} className={`rounded-xl p-3 border ${_BAND_TONE[h.band] || _BAND_TONE.ok}`} data-testid={`trust-hint-${h.key}`}>
              <div className="flex items-center justify-between">
                <span className="font-bold text-sm">{h.label}</span>
                <span className="text-[10px] font-bold uppercase tracking-wider">{_BAND_LABEL[h.band] || ""}</span>
              </div>
              <p className="text-xs mt-1 opacity-80">{h.hint}</p>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-gray-400">
          We don&apos;t share the exact formula to keep the score fair. The factors shown here are the only inputs you can influence.
        </p>
      </div>
    </div>
  );
}

function OrderTable({ orders }) {
  const nav = useNavigate();
  if (!orders.length) return <div className="text-gray-500 text-sm">No orders yet.</div>;
  return (
    <div className="mv-table-wrap">
      <table className="w-full text-sm" data-testid="seller-orders-table">
        <thead className="text-left text-xs uppercase tracking-wider text-gray-500">
          <tr className="border-b border-gray-100"><th className="py-2">Service</th><th>Status</th><th>Earnings</th><th>Date</th><th></th></tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.id} className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer" onClick={() => nav(`/dashboard/seller/orders/${o.id}`)}>
              <td className="py-3 pr-3 font-bold text-mv-secondary">{o.service_title}</td>
              <td><span className="px-2 py-0.5 rounded-full bg-mv-primary/10 text-mv-primary text-xs font-bold capitalize">{o.status.replace(/_/g, " ")}</span></td>
              <td className="font-bold">${o.seller_earnings}</td>
              <td className="text-gray-500">{new Date(o.created_at).toLocaleDateString()}</td>
              <td><Link to={`/dashboard/seller/orders/${o.id}`} className="text-mv-primary font-bold text-xs">Open →</Link></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OrdersPage() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/orders").then(({ data }) => setOrders(data)); }, []);
  return <Card title="All orders"><OrderTable orders={orders} /></Card>;
}

function MyServices() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/services?seller_id=me&limit=50").then(() => {});
    // Get my services via the listing api filtered by seller_id (need my id)
    api.get("/auth/me").then(({ data }) => {
      api.get(`/services?seller_id=${data.id}&limit=50`).then(({ data: r }) => setItems(r.items));
    });
  }, []);
  return (
    <Card title="My services" action={<Link to="/dashboard/seller/services/new"><Button className="bg-mv-primary text-white font-bold" data-testid="new-service-btn"><Plus className="mr-1" />New service</Button></Link>}>
      {items.length === 0 ? <div className="text-sm text-gray-500">No services yet. Create your first listing.</div> :
        <ul className="divide-y divide-gray-100" data-testid="my-services-list">
          {items.map((s) => (
            <li key={s.id} className="py-3 flex items-center gap-3" data-testid={`my-service-${s.id}`}>
              <img src={s.images?.[0]} alt="" className="w-14 h-14 rounded-lg object-cover" />
              <div className="flex-1 min-w-0">
                <Link to={`/service/${s.id}`} className="font-bold text-mv-secondary hover:text-mv-primary truncate block">{s.title}</Link>
                <div className="text-xs text-gray-500">${s.starting_price} · {s.order_count} orders · {s.review_count} reviews</div>
              </div>
              <span className="text-[10px] uppercase font-bold text-mv-primary tracking-wider hidden sm:inline">{s.status}</span>
              <Link to={`/dashboard/seller/services/${s.id}/edit`}>
                <Button variant="outline" size="sm" data-testid={`edit-service-${s.id}`} className="text-xs">
                  <PencilSimple size={14} className="mr-1" /> Edit
                </Button>
              </Link>
            </li>
          ))}
        </ul>}
    </Card>
  );
}

// --- NewService + EditService — thin wrappers around ServiceForm ----------

function NewService() {
  const [meId, setMeId] = useState(null);
  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setMeId(data?.id)).catch(() => {});
  }, []);
  return (
    <Card title="Create a service">
      <ServiceForm mode="create" meId={meId} />
    </Card>
  );
}

function EditService() {
  const { id } = useParams();
  const nav = useNavigate();
  const [meId, setMeId] = useState(null);
  const [svc, setSvc] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setMeId(data?.id)).catch(() => {});
    api.get(`/services/${id}`)
      .then(({ data }) => setSvc(data))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Service not found"));
  }, [id]);
  if (err) {
    return <Card title="Edit service"><div className="text-sm text-rose-600" data-testid="es-error">{err}</div></Card>;
  }
  if (!svc || !meId) return <Card title="Edit service"><div className="text-sm text-gray-500">Loading…</div></Card>;
  if (svc.seller_id !== meId) {
    return (
      <Card title="Edit service">
        <div className="text-sm text-rose-600" data-testid="es-forbidden">
          You can only edit your own services.
        </div>
        <Button variant="outline" className="mt-3" onClick={() => nav("/dashboard/seller/services")}>
          Back to my services
        </Button>
      </Card>
    );
  }
  return (
    <Card title={`Edit service — ${svc.title}`}>
      <ServiceForm mode="edit" meId={meId} service={svc} />
    </Card>
  );
}

function Earnings() {
  const [data, setData] = useState(null);
  const [tips, setTips] = useState([]);
  const [amount, setAmount] = useState(50);
  const [connectStatus, setConnectStatus] = useState(null);
  const load = () => {
    api.get("/payments/payouts/my").then(({ data }) => setData(data));
    api.get("/tips/my").then(({ data }) => setTips(data || [])).catch(() => {});
    api.get("/payments/connect/status").then(({ data }) => setConnectStatus(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  // Sync from Stripe when returning from onboarding (?stripe=return)
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("stripe") === "return") {
      api.post("/payments/connect/refresh").then(() => {
        toast.success("Stripe status updated");
        load();
      }).catch((e) => {
        toast.error(formatApiError(e.response?.data?.detail) || "Couldn't refresh Stripe status");
      });
    }
  }, []);
  const refreshConnect = async () => {
    try {
      const { data } = await api.post("/payments/connect/refresh");
      toast.success(data.synced ? "Synced with Stripe" : "Mock mode — nothing to sync");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't refresh Stripe status");
    }
  };
  const connect = async () => {
    try {
      const { data } = await api.post("/payments/connect/onboarding");
      // Hosted Stripe URLs always start with connect.stripe.com (or a
      // verified-redirect). The mocked path returns "…/mock". We must
      // never silently treat the mock URL as real onboarding.
      const url = data.onboarding_url || "";
      const isReal = !data.mocked_stripe_call &&
        url.startsWith("https://connect.stripe.com/") &&
        !url.includes("/mock");
      if (isReal) {
        toast.success("Redirecting to Stripe…");
        window.location.href = url;
      } else {
        toast.message(
          "Stripe Connect is in test scaffolding on this environment.",
          { description: "Real onboarding becomes available once an sk_test_/sk_live_ key is configured." },
        );
        load();
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const request = async () => {
    try { await api.post(`/payments/payouts/request`, { amount: Number(amount) }); toast.success("Payout requested"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!data) return null;
  // Resolve the Stripe Connect display state — used both in the badge and in
  // the dedicated Connect status card below.
  const cs = connectStatus || {};
  const realMode = cs.real_stripe_enabled;
  let stripeLabel = "Not connected";
  let stripeBadge = "bg-amber-50 text-amber-700 border border-amber-200";
  if (cs.stripe_account_id) {
    if (cs.charges_enabled && cs.payouts_enabled) {
      stripeLabel = "Fully active"; stripeBadge = "bg-mv-primary/10 text-mv-primary border border-mv-primary/30";
    } else if (cs.details_submitted) {
      stripeLabel = "Verification pending"; stripeBadge = "bg-blue-50 text-blue-700 border border-blue-200";
    } else {
      stripeLabel = "Onboarding incomplete"; stripeBadge = "bg-amber-50 text-amber-700 border border-amber-200";
    }
  }
  return (
    <>
      <div className="grid sm:grid-cols-3 gap-4">
        <Stat label="Available" value={`$${data.earnings_balance.toFixed(0)}`} icon={Wallet} />
        <Stat label="Lifetime" value={`$${data.lifetime_earnings.toFixed(0)}`} icon={ChartLineUp} />
        <Stat label="Stripe" value={stripeLabel} icon={CheckCircle} />
      </div>

      {/* STRIPE CONNECT — dedicated, transparent status card. */}
      <Card title="Stripe Connect" action={
        <div className="flex gap-2 items-center">
          {cs.stripe_account_id && (
            <Button
              type="button" variant="outline" size="sm" onClick={refreshConnect}
              data-testid="stripe-refresh-btn" className="text-xs"
            >
              Refresh status
            </Button>
          )}
          {!cs.charges_enabled && (
            <Button
              type="button" onClick={connect}
              className="bg-mv-secondary text-white"
              data-testid="connect-stripe-btn"
            >
              {cs.stripe_account_id ? "Continue onboarding" : "Connect Stripe"}
            </Button>
          )}
        </div>
      }>
        <div className="space-y-3 text-sm" data-testid="stripe-connect-card">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${stripeBadge}`}
                  data-testid="stripe-status-badge">
              {stripeLabel}
            </span>
            {!realMode && (
              <span className="text-[11px] text-gray-500 inline-flex items-center gap-1"
                    data-testid="stripe-test-scaffolding-notice">
                <Warning size={12} className="text-amber-500" weight="duotone" />
                Test scaffolding — operator must set a real STRIPE_API_KEY for live onboarding
              </span>
            )}
          </div>
          <div className="grid sm:grid-cols-3 gap-2 text-xs">
            <div className="rounded-lg bg-gray-50 px-3 py-2">
              <div className="text-gray-500 uppercase tracking-wider text-[10px]">Charges</div>
              <div className={`font-bold ${cs.charges_enabled ? "text-mv-primary" : "text-gray-700"}`}
                   data-testid="stripe-charges-state">
                {cs.charges_enabled ? "Enabled" : "Disabled"}
              </div>
            </div>
            <div className="rounded-lg bg-gray-50 px-3 py-2">
              <div className="text-gray-500 uppercase tracking-wider text-[10px]">Payouts</div>
              <div className={`font-bold ${cs.payouts_enabled ? "text-mv-primary" : "text-gray-700"}`}
                   data-testid="stripe-payouts-state">
                {cs.payouts_enabled ? "Enabled" : "Disabled"}
              </div>
            </div>
            <div className="rounded-lg bg-gray-50 px-3 py-2">
              <div className="text-gray-500 uppercase tracking-wider text-[10px]">Details submitted</div>
              <div className={`font-bold ${cs.details_submitted ? "text-mv-primary" : "text-gray-700"}`}
                   data-testid="stripe-details-state">
                {cs.details_submitted ? "Yes" : "No"}
              </div>
            </div>
          </div>
          {cs.stripe_account_id && (
            <div className="text-[11px] text-gray-400">Account: {cs.stripe_account_id}</div>
          )}
        </div>
      </Card>

      {/* Earnings breakdown — service vs tip. lifetime_earnings ALREADY includes tips;
          service portion = lifetime_earnings - lifetime_tip_earnings. NEVER added.
          Single source of truth: /api/payments/payouts/my returns lifetime_service_earnings derived. */}
      {(data.lifetime_tip_earnings || 0) > 0 && (
        <Card title="Earnings breakdown">
          <div className="grid sm:grid-cols-2 gap-3 text-sm" data-testid="earnings-breakdown">
            <div className="rounded-xl bg-gray-50 px-4 py-3">
              <div className="text-xs uppercase tracking-wider text-gray-500">Service earnings</div>
              <div className="font-display font-black text-2xl text-mv-secondary mt-1"
                   data-testid="service-earnings">
                ${(data.lifetime_service_earnings || 0).toFixed(2)}
              </div>
            </div>
            <div className="rounded-xl bg-mv-primary/5 px-4 py-3 border border-mv-primary/20">
              <div className="text-xs uppercase tracking-wider text-mv-primary">Tip earnings</div>
              <div className="font-display font-black text-2xl text-mv-primary mt-1"
                   data-testid="tip-earnings">
                ${(data.lifetime_tip_earnings || 0).toFixed(2)}
              </div>
              <div className="text-[11px] text-gray-500 mt-0.5">After {17}% MarketVyne fee on tips</div>
            </div>
          </div>
        </Card>
      )}

      {/* Tips received — separate, labeled, never folded into service revenue */}
      {tips.length > 0 && (
        <Card title="Tips received">
          <table className="w-full text-sm" data-testid="tips-table">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-gray-500">
              <th>Date</th><th>Label</th><th>Order</th><th>Gross</th><th>Fee</th><th>You receive</th><th>Status</th>
            </tr></thead>
            <tbody>
              {tips.map((t) => (
                <tr key={t.id} className="border-t border-gray-100" data-testid={`tip-row-${t.id}`}>
                  <td className="py-2">{new Date(t.created_at).toLocaleDateString()}</td>
                  <td className="py-2 font-semibold text-mv-primary">Tip from buyer</td>
                  <td className="py-2">
                    <a href={`/dashboard/seller/orders/${t.order_id}`} className="text-mv-secondary underline">
                      {t.order_id.slice(0, 8)}
                    </a>
                  </td>
                  <td className="py-2">${Number(t.gross_tip_amount).toFixed(2)}</td>
                  <td className="py-2 text-gray-500">${Number(t.platform_fee_amount).toFixed(2)}</td>
                  <td className="py-2 font-display font-bold text-mv-secondary">
                    ${Number(t.seller_tip_earnings).toFixed(2)}
                  </td>
                  <td className="py-2 capitalize">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      t.status === "paid" ? "bg-mv-primary/15 text-mv-primary"
                      : t.status === "pending" ? "bg-amber-100 text-amber-700"
                      : "bg-gray-100 text-gray-500"
                    }`}>{t.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <Card title="Payouts" action={
        cs.payouts_enabled ?
        <div className="flex items-center gap-2">
          <Input type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))} className="w-28" />
          <Button onClick={request} className="bg-mv-primary text-white" data-testid="request-payout-btn">Request payout</Button>
        </div> :
        <span className="text-xs text-gray-500">Finish Stripe onboarding to request a payout</span>
      }>
        {data.payouts.length === 0 ? <div className="text-sm text-gray-500">No payouts yet.</div> :
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-gray-500"><th>Date</th><th>Amount</th><th>Status</th></tr></thead>
            <tbody>{data.payouts.map((p) => <tr key={p.id} className="border-t border-gray-100"><td className="py-2">{new Date(p.created_at).toLocaleDateString()}</td><td>${p.amount}</td><td><span className="capitalize">{p.status}</span></td></tr>)}</tbody>
          </table>}
      </Card>
    </>
  );
}

function Settings() {
  const { user, refresh } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = React.useRef(null);

  const upload = async (file) => {
    if (!file) return;
    // Client-side guards mirror the server: type + size.
    const allowed = ["image/jpeg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      toast.error("Photo must be JPG, PNG, or WebP.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo is too large (max 5MB).");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "avatars");
      const { data } = await api.post("/files", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = `${API}/files/${data.file_id}`;
      await api.patch("/auth/me", { avatar_url: url });
      await refresh?.();
      toast.success("Profile photo updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = async () => {
    if (!window.confirm("Remove your profile photo?")) return;
    setBusy(true);
    try {
      await api.patch("/auth/me", { avatar_url: "" });
      await refresh?.();
      toast.success("Photo removed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <>
      <Card title="Profile photo">
        <div className="flex items-center gap-5 flex-wrap" data-testid="seller-settings-avatar-block">
          <Avatar
            src={user?.avatar_url}
            name={user?.name || ""}
            size={96}
            rounded="rounded-2xl"
            testId="seller-settings-avatar"
          />
          <div className="space-y-1.5">
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => upload(e.target.files?.[0])}
              data-testid="avatar-file-input"
            />
            <div className="flex gap-2 flex-wrap">
              <Button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading || busy}
                className="bg-mv-primary text-white"
                data-testid="upload-avatar-btn"
              >
                {uploading ? "Uploading…" : user?.avatar_url ? "Replace photo" : "Upload photo"}
              </Button>
              {user?.avatar_url && (
                <Button
                  type="button" variant="outline" onClick={removePhoto} disabled={busy || uploading}
                  data-testid="remove-avatar-btn"
                >
                  <Trash size={14} className="mr-1.5" /> Remove
                </Button>
              )}
            </div>
            <p className="text-xs text-gray-500">
              Square image works best. JPG, PNG, or WebP up to 5 MB.
            </p>
          </div>
        </div>
      </Card>
    </>
  );
}

export default function SellerDashboard() {
  return (
    <Routes>
      <Route element={<DashboardShell links={links} title="Seller dashboard" />}>
        <Route index element={<Overview />} />
        <Route path="services" element={<MyServices />} />
        <Route path="services/new" element={<NewService />} />
        <Route path="services/:id/edit" element={<EditService />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="orders/:id" element={<OrderDetail />} />
        <Route path="earnings" element={<Earnings />} />
        <Route path="settings" element={<Settings />} />
      </Route>
    </Routes>
  );
}
